import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { useLang } from "../../context/LangContext";
import { useCMS } from "../../context/CMSContext";
import { ArrowRight } from "../common/Icon";
import styles from "./Hero.module.css";

const DRAG_THRESHOLD = 50;

export default function Hero() {
  const { t, lang } = useLang(); // lang = 'en' | 'tr'
  const { cms } = useCMS();

  // ── Resolve slides from CMS, fall back to static translations ─────────────
  const cmsSlides = cms?.hero?.slides;

  const slides = cmsSlides
    ? cmsSlides.map((s) => ({
        title: s[`title_${lang}`] || s.title_en || "",
        subtitle: s[`subtitle_${lang}`] || s.subtitle_en || "",
        cta: s[`cta_${lang}`] || s.cta_en || "Get Started",
        ctaHref: s.ctaHref || "#download",
      }))
    : t.hero.slides;

  const imageSlides = cmsSlides || [];

  const [current, setCurrent] = useState(0);
  const [isTransitioning, setTransition] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartX = useRef(0);

  const goTo = useCallback(
    (index) => {
      if (isTransitioning || index === current || slides.length === 0) return;
      setTransition(true);
      setTimeout(() => {
        setCurrent(index);
        setTransition(false);
      }, 380);
    },
    [isTransitioning, current, slides.length],
  );

  const goNext = useCallback(
    () => goTo((current + 1) % slides.length),
    [current, slides.length, goTo],
  );
  const goPrev = useCallback(
    () => goTo((current - 1 + slides.length) % slides.length),
    [current, slides.length, goTo],
  );

  useEffect(() => {
    if (slides.length === 0) return;
    const id = setInterval(goNext, 5000);
    return () => clearInterval(id);
  }, [goNext, slides.length]);

  // Reset index if CMS changes slide count
  useEffect(() => {
    if (current >= slides.length && slides.length > 0) setCurrent(0);
  }, [slides.length, current]);

  const handleMouseDown = useCallback((e) => {
    dragStartX.current = e.clientX;
    setIsDragging(true);
    e.preventDefault();
  }, []);
  const handleMouseUp = useCallback(
    (e) => {
      if (!isDragging) return;
      setIsDragging(false);
      const d = e.clientX - dragStartX.current;
      if (Math.abs(d) >= DRAG_THRESHOLD) d < 0 ? goNext() : goPrev();
    },
    [isDragging, goNext, goPrev],
  );
  const handleMouseLeave = useCallback(() => {
    if (isDragging) setIsDragging(false);
  }, [isDragging]);
  const handleTouchStart = useCallback((e) => {
    dragStartX.current = e.touches[0].clientX;
  }, []);
  const handleTouchEnd = useCallback(
    (e) => {
      const d = e.changedTouches[0].clientX - dragStartX.current;
      if (Math.abs(d) >= DRAG_THRESHOLD) d < 0 ? goNext() : goPrev();
    },
    [goNext, goPrev],
  );

  if (slides.length === 0) return null;

  const slide = slides[current];
  const imageSlide = imageSlides[current];

  return (
    <section id="hero" className={styles.hero} aria-label="Hero banner">
      <div className={styles.wrapper}>
        {/* ── Left panel ─────────────────────────────────────────────── */}
        <div className={styles.textPanel}>
          <span className={styles.brandLabel}>BITAKAS</span>

          <h1 className={styles.title} key={`title-${current}`}>
            {slide.title}
          </h1>
          <p className={styles.subtitle} key={`sub-${current}`}>
            {slide.subtitle}
          </p>

          <div className={styles.ctaGroup}>
            <Link
              to={slide.ctaHref || "#download"}
              className={styles.ctaPrimary}
            >
              {slide.cta}
              <ArrowRight size={18} />
            </Link>
          </div>

          <div className={styles.counter}>
            <span className={styles.counterCurrent}>
              {String(current + 1).padStart(2, "0")}
            </span>
            <span className={styles.counterDivider} />
            <span className={styles.counterTotal}>
              {String(slides.length).padStart(2, "0")}
            </span>
          </div>
        </div>

        {/* ── Right panel (image slider) ──────────────────────────────── */}
        <div
          className={`${styles.sliderPanel} ${isDragging ? styles.dragging : ""}`}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <div className={styles.slideTrack}>
            {(imageSlides.length ? imageSlides : slides).map((slide, i) => (
              <div
                key={slide.id || i}
                className={`${styles.slide} ${i === current ? styles.slideActive : ""}`}
                aria-hidden={i !== current}
              >
                {slide.imageUrl ? (
                  <img
                    src={slide.imageUrl}
                    alt={`Banner ${i + 1}`}
                    className={styles.slideImage}
                    draggable={false}
                  />
                ) : (
                  <div
                    className={styles.slideFallback}
                    style={{
                      background:
                        slide.fallbackGradient ||
                        "linear-gradient(135deg,#D60B0B,#1718BF)",
                    }}
                  />
                )}
              </div>
            ))}
          </div>

          <div className={styles.dragHint}>
            <span>&#8592;</span>
            <span>drag</span>
            <span>&#8594;</span>
          </div>

          <button
            className={`${styles.arrow} ${styles.arrowLeft}`}
            onClick={goPrev}
            aria-label="Previous slide"
          >
            &#8249;
          </button>
          <button
            className={`${styles.arrow} ${styles.arrowRight}`}
            onClick={goNext}
            aria-label="Next slide"
          >
            &#8250;
          </button>

          <div className={styles.dots} role="tablist">
            {slides.map((_, i) => (
              <button
                key={i}
                role="tab"
                aria-selected={i === current}
                className={`${styles.dot} ${i === current ? styles.dotActive : ""}`}
                onClick={() => goTo(i)}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
